import { motion, AnimatePresence } from "framer-motion";
import { Link } from "wouter";
import { 
  ArrowLeft, ArrowRight, Filter, X, 
  PenTool, Search, Mail, Server as ServerIcon, Globe as GlobeIcon, 
  Layers, Cloud, Shield, Settings, Smartphone, Code, 
  ShoppingCart, Briefcase, BarChart, Cpu,
  // Remove unused imports to fix lint errors
  // Smartwatch is not available in lucide-react
} from "lucide-react";
import ServiceCard from "@/components/ServiceCard";
import { SeoHead } from '@/components/SeoHead';
import servicesData from "@/data/services.json";
import { useState, useEffect } from "react";

// Types
interface Service {
  title: string;
  slug: string;
  description: string;
  icon: string | JSX.Element;
  gradient: string;
  features: string[];
  technologies: string[];
  process?: string[];
  category: string;
}

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function OurServices() {
  // Categories for filtering
  const categories = [
    'All',
    'Development',
    'Design',
    'Marketing',
    'Cloud',
    'Security',
    'Support'
  ];

  const [activeCategory, setActiveCategory] = useState('All');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Service to icon mapping
  const serviceIcons: Record<string, JSX.Element> = {
    'web-development': <Code className="w-12 h-12" />,
    'mobile-apps': <Smartphone className="w-12 h-12" />,
    'ecommerce': <ShoppingCart className="w-12 h-12" />,
    'ui-ux': <PenTool className="w-12 h-12" />,
    'seo': <Search className="w-12 h-12" />,
    'email-hosting': <Mail className="w-12 h-12" />,
    'web-hosting': <ServerIcon className="w-12 h-12" />,
    'domain-registration': <GlobeIcon className="w-12 h-12" />,
    'erp-crm': <Layers className="w-12 h-12" />,
    'cloud-services': <Cloud className="w-12 h-12" />,
    'cyber-security': <Shield className="w-12 h-12" />,
    'maintenance-support': <Settings className="w-12 h-12" />,
    'custom-apps': <Smartphone className="w-12 h-12" />,
    'software-development': <Code className="w-12 h-12" />,
    'digital-marketing': <BarChart className="w-12 h-12" />,
    'it-consulting': <Briefcase className="w-12 h-12" />,
    'data-analytics': <BarChart className="w-12 h-12" />,
    'iot-solutions': <Cpu className="w-12 h-12" />
  };
  
  // Get icon for service
  const getServiceIcon = (slug: string): JSX.Element => {
    return serviceIcons[slug] || <Code className="w-12 h-12" />;
  };

  // Add category to each service
  const allServices: Service[] = [
    ...servicesData.map(service => ({
      ...service,
      category: (service as any).category || 'Development', // Default to Development if no category exists
      icon: getServiceIcon(service.slug)
    })),
    // Additional services with categories and JSX icons
    {
      title: "UI/UX Design",
      slug: "ui-ux",
      description: "Beautiful, intuitive interfaces that enhance user experience and drive engagement.",
      icon: <PenTool className="w-12 h-12" />,
      gradient: "from-pink-500 to-rose-500",
      features: ["User Research", "Wireframing", "Prototyping", "UI Design", "UX Strategy", "Usability Testing"],
      technologies: ["Figma", "Sketch", "Adobe XD", "InVision", "Framer", "Webflow"],
      category: "Design"
    },
    {
      title: "Mobile App Development",
      slug: "mobile-apps",
      description: "Custom mobile applications for iOS and Android platforms with native performance.",
      icon: <Smartphone className="w-12 h-12" />,
      gradient: "from-blue-500 to-cyan-400",
      features: ["Native iOS & Android", "Cross-Platform", "App Store Optimization", "Push Notifications", "API Integration", "Offline Support"],
      technologies: ["React Native", "Flutter", "Swift", "Kotlin", "Firebase", "Redux"],
      category: "Development"
    },
    {
      title: "Cloud Services",
      slug: "cloud-services",
      description: "Scalable cloud infrastructure and services to power your applications.",
      icon: <Cloud className="w-12 h-12" />,
      gradient: "from-amber-500 to-yellow-400",
      features: ["Cloud Migration", "Serverless Architecture", "Containerization", "DevOps Automation", "Cloud Security", "Cost Optimization"],
      technologies: ["AWS", "Azure", "Google Cloud", "Docker", "Kubernetes", "Terraform"],
      category: "Cloud"
    },
    {
      title: "Cyber Security",
      slug: "cyber-security",
      description: "Comprehensive security solutions to protect your digital assets and data.",
      icon: <Shield className="w-12 h-12" />,
      gradient: "from-emerald-500 to-teal-400",
      features: ["Vulnerability Assessment", "Penetration Testing", "Security Audits", "Incident Response", "Security Training", "Compliance"],
      technologies: ["SIEM", "Firewall", "IDS/IPS", "WAF", "EDR", "Zero Trust"],
      category: "Security"
    },
    {
      title: "Digital Marketing",
      slug: "digital-marketing",
      description: "Data-driven marketing strategies to grow your online presence and reach your target audience.",
      icon: <BarChart className="w-12 h-12" />,
      gradient: "from-purple-500 to-indigo-500",
      features: ["SEO", "PPC Advertising", "Social Media Marketing", "Content Strategy", "Email Marketing", "Analytics"],
      technologies: ["Google Ads", "Facebook Ads", "Google Analytics", "HubSpot", "Mailchimp", "Hootsuite"],
      category: "Marketing"
    },
    {
      title: "IT Consulting",
      slug: "it-consulting",
      description: "Expert technology consulting to align your IT strategy with business goals.",
      icon: <Briefcase className="w-12 h-12" />,
      gradient: "from-red-500 to-orange-400",
      features: ["IT Strategy", "Digital Transformation", "Technology Roadmap", "Vendor Selection", "Process Optimization", "Cost Reduction"],
      technologies: ["ITIL", "COBIT", "Agile", "DevOps", "Cloud Architecture", "Enterprise Architecture"],
      category: "Support"
    },
    {
      title: "SEO Services",
      slug: "seo",
      description: "Improve your search engine rankings and drive organic traffic to your website.",
      icon: "fas fa-search",
      gradient: "from-green-500 to-emerald-500",
      features: ["Keyword Research", "On-Page SEO", "Technical SEO", "Link Building", "Local SEO", "Analytics & Reporting"],
      technologies: ["Google Analytics", "Google Search Console", "SEMrush", "Ahrefs", "Moz", "Screaming Frog"],
      category: "Marketing"
    },
    {
      title: "Email Hosting",
      slug: "email-hosting",
      description: "Professional email hosting with enterprise-grade security and reliability.",
      icon: "fas fa-envelope",
      gradient: "from-blue-400 to-indigo-500",
      features: ["Custom Domain Email", "Spam Protection", "Email Encryption", "Webmail Access", "Email Forwarding", "Large Storage"],
      technologies: ["Google Workspace", "Microsoft 365", "Zoho Mail", "cPanel", "Plesk", "Roundcube"],
      category: "Support"
    },
    {
      title: "Web Hosting",
      slug: "web-hosting",
      description: "Fast, secure, and reliable web hosting solutions for businesses of all sizes.",
      icon: "fas fa-server",
      gradient: "from-cyan-500 to-blue-500",
      features: ["99.9% Uptime", "SSL Certificates", "Daily Backups", "24/7 Support", "One-Click Installs", "Scalable Resources"],
      technologies: ["cPanel", "Plesk", "CloudLinux", "LiteSpeed", "WordPress", "Joomla"],
      category: "Support"
    },
    {
      title: "Domain Registration",
      slug: "domain-registration",
      description: "Secure your perfect domain name with our easy registration service.",
      icon: "fas fa-globe-americas",
      gradient: "from-purple-500 to-indigo-500",
      features: ["Domain Search", "Bulk Registration", "Domain Transfer", "WHOIS Privacy", "DNS Management", "Auto-Renewal"],
      technologies: [".com", ".net", ".org", ".io", ".ai", "New gTLDs"],
      category: "Support"
    },
    {
      title: "ERP / CRM Systems",
      slug: "erp-crm",
      description: "Streamline your business processes with custom ERP and CRM solutions.",
      icon: "fas fa-project-diagram",
      gradient: "from-teal-500 to-emerald-500",
      features: ["Business Process Automation", "Customer Management", "Inventory Control", "Sales Tracking", "Reporting & Analytics", "Third-party Integration"],
      technologies: ["Salesforce", "HubSpot", "Odoo", "SAP", "Microsoft Dynamics", "Zoho"],
      category: "Development"
    },
    {
      title: "Cloud & DevOps",
      slug: "cloud-services",
      description: "Scalable cloud infrastructure and services to power your business applications.",
      icon: "fas fa-cloud",
      gradient: "from-blue-400 to-indigo-600",
      features: ["Cloud Migration", "Serverless Architecture", "Cloud Security", "DevOps Automation", "Containerization", "Cloud Cost Optimization"],
      technologies: ["AWS", "Azure", "Google Cloud", "Docker", "Kubernetes", "Terraform"],
      category: "Cloud"
    },
    {
      title: "Cyber Security",
      slug: "cyber-security",
      description: "Comprehensive security solutions to protect your digital assets and data.",
      icon: "fas fa-shield-alt",
      gradient: "from-emerald-500 to-teal-400",
      features: ["Vulnerability Assessment", "Penetration Testing", "Security Audits", "Incident Response", "Security Training", "Compliance Management"],
      technologies: ["OWASP", "NIST", "ISO 27001", "GDPR", "SIEM", "Firewalls"],
      category: "Security"
    },
    {
      title: "Maintenance & Support",
      slug: "maintenance-support",
      description: "Reliable IT support and maintenance services to keep your systems running smoothly.",
      icon: "fas fa-tools",
      gradient: "from-amber-500 to-orange-500",
      features: ["24/7 Monitoring", "Help Desk Support", "System Updates", "Backup Solutions", "Network Management", "Security Patches"],
      technologies: ["Windows Server", "Linux", "Active Directory", "Office 365", "VMware", "Backup Solutions"],
      category: "Support"
    },
    {
      title: "Custom App Development",
      slug: "custom-apps",
      description: "Tailored applications designed to solve your specific business challenges.",
      icon: "fas fa-mobile",
      gradient: "from-blue-600 to-indigo-500",
      features: ["Requirement Analysis", "Custom Development", "API Integration", "Testing & QA", "Deployment", "Ongoing Support"],
      technologies: ["React Native", "Flutter", "Swift", "Kotlin", "React", "Node.js"],
      category: "Development"
    }
  ];

  return (
    <>
      <SeoHead 
        title="Our Services | Custom Software Development | ZeeX Soft"
        description="Explore our comprehensive software development services including web apps, mobile apps, and enterprise solutions in UAE."
        keywords="web development, mobile apps, software solutions, UAE tech company"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-background to-cyan-500/5 -z-10" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <motion.h1 
                className="font-orbitron font-bold text-4xl md:text-5xl lg:text-6xl mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-400">Services</span>
              </motion.h1>
              <motion.p 
                className="text-xl text-muted-foreground max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Comprehensive digital solutions tailored to help your business grow and succeed in the digital age.
              </motion.p>
              <motion.div 
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Link 
                  href="/contact" 
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 transition-all duration-300"
                >
                  Get a Free Consultation
                  <ArrowLeft className="ml-2 -mr-1 h-5 w-5 transform rotate-180" />
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Showcase */}
        <section className="py-20 bg-background relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 -z-10 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Our Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Digital Solutions</span></h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">Discover how our expert services can transform your business and drive growth in the digital landscape.</p>
            </motion.div>

            {/* Category Filter - Mobile */}
            <div className="lg:hidden mb-8">
              <button 
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-sm border border-gray-200 dark:border-gray-700 text-sm font-medium mx-auto"
              >
                {isFilterOpen ? <X size={16} /> : <Filter size={16} />}
                {activeCategory === 'All' ? 'All Categories' : activeCategory}
              </button>
              
              <AnimatePresence>
                {isFilterOpen && (
                  <motion.div 
                    className="mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 absolute left-4 right-4 z-10 border border-gray-200 dark:border-gray-700"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <div className="grid grid-cols-2 gap-2">
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveCategory(category);
                            setIsFilterOpen(false);
                          }}
                          className={`px-3 py-2 text-sm rounded-lg text-left transition-colors ${
                            activeCategory === category
                              ? 'bg-blue-600 text-white'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Category Filter - Desktop */}
            <div className="hidden lg:flex justify-center gap-2 mb-12 overflow-x-auto pb-4 scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all ${
                    activeCategory === category
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-md'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Services Grid */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-50px" }}
            >
              <AnimatePresence>
                {allServices
                  .filter(service => activeCategory === 'All' || service.category === activeCategory)
                  .map((service) => (
                    <motion.div
                      key={service.slug}
                      variants={item}
                      className="h-full"
                      whileHover={{ y: -5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <ServiceCard
                        title={service.title}
                        description={service.description}
                        icon={service.icon}
                        href={`/${service.slug}`}
                        gradient={service.gradient}
                      />
                    </motion.div>
                  ))}
              </AnimatePresence>
            </motion.div>

            {/* Empty State */}
            {allServices.filter(service => activeCategory === 'All' || service.category === activeCategory).length === 0 && (
              <motion.div 
                className="text-center py-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
                  <Filter className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No services found</h3>
                <p className="text-muted-foreground mb-4">We couldn't find any services matching your filter.</p>
                <button
                  onClick={() => setActiveCategory('All')}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Clear filters
                </button>
              </motion.div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-orbitron font-bold text-3xl md:text-4xl mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
              Let's discuss how our services can help you achieve your business goals.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-blue-600 bg-white hover:bg-gray-100 transition-colors duration-300"
              >
                Get Started
              </Link>
              <a 
                href="tel:+971503357283" 
                className="text-blue-500 hover:underline" 
                rel="noreferrer"
              >
                +971 50 335 7283
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
